/**
 * The two mutually exclusive native roots, and which one is on screen. Split out of
 * NativeRootController so a module that only reads this does not pull in that module's
 * native-navigation and store imports.
 */
export type RootKind = "tabs" | "auth";

/** Deeper than any stack the app builds. `pop` clamps to the real depth, so this empties it. */
export const POP_TO_ROOT_COUNT = 50;

// The pre-auth stack roots at the welcome screen. Sign in, sign up and recovery push onto it.
export const AUTH_ROOT_PATH = "/";

// Lets the pre-auth stack be targeted from outside its own webview, as the tab stacks are.
export const AUTH_STACK_ID = "auth-stack";

/** One view of the pre-auth stack, and whether it shows the native bar. */
export interface AuthStackView {
  path: string;
  barVisible: boolean;
}

/**
 * The pre-auth stack's views, bottom first. Welcome is always at the bottom, so a reset to
 * AUTH_ROOT_PATH always has somewhere to land. A pending auth link sits on it with a bar, and
 * so a back button. A pending guest link is a transient spinner, so it hides the bar instead.
 * An auth link takes precedence if both are somehow pending.
 */
export function authStackViews(authPath: string | undefined, guestPath: string | undefined): AuthStackView[] {
  const welcome: AuthStackView = { path: AUTH_ROOT_PATH, barVisible: false };
  if (authPath !== undefined && authPath !== AUTH_ROOT_PATH) return [welcome, { path: authPath, barVisible: true }];
  if (authPath === undefined && guestPath !== undefined && guestPath !== AUTH_ROOT_PATH) {
    return [welcome, { path: guestPath, barVisible: false }];
  }
  return [welcome];
}

// Module state, not a ref. Read from outside the React tree.
let presentedRoot: RootKind | null = null;

// True across a root swap, while `presentedRoot` still names the root being torn down.
let reconciling = false;

// True while a pre-auth screen keeps the auth root up over a session it has already opened.
let authRootHeld = false;

// True across a tab reflow, while the bar's old tab webviews are being dropped and the new
// ones installed. Set by `syncPresentedTabs`, which owns the reflow.
let reflowing = false;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Subscribes to changes in any of the values above. Returns an unsubscribe function. */
export function subscribeNativeRootState(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The root on screen, or null before the first present. Stale while a swap is in flight. */
export function presentedNativeRoot(): RootKind | null {
  return presentedRoot;
}

export function setPresentedNativeRoot(root: RootKind | null): void {
  if (presentedRoot === root) return;
  presentedRoot = root;
  notify();
}

export function isRootSwapInFlight(): boolean {
  return reconciling;
}

export function setRootSwapInFlight(value: boolean): void {
  if (reconciling === value) return;
  reconciling = value;
  notify();
}

/**
 * Whether a pre-auth screen is holding the auth root up although a session is open. The
 * sign-up setup screen opens the session seconds in, while the user may still be watching
 * its video, and lets go when they skip.
 */
export function isAuthRootHeld(): boolean {
  return authRootHeld;
}

export function setAuthRootHeld(value: boolean): void {
  if (authRootHeld === value) return;
  authRootHeld = value;
  notify();
}

export function isTabReflowInFlight(): boolean {
  return reflowing;
}

export function setTabReflowInFlight(value: boolean): void {
  if (reflowing === value) return;
  reflowing = value;
  notify();
}

/**
 * Whether a native transition is mid-flight, so the components on screen are not the ones
 * this module's bookkeeping names.
 */
export function isNativeNavTransitionInFlight(): boolean {
  return reconciling || reflowing;
}

/**
 * Resolves once no native transition is in flight.
 *
 * A root swap tears its old root down before raising the new one, and a reflow drops the
 * old tab webviews before installing the new set. A call that lands in either window is
 * addressed to a component that is being created or destroyed, which breaks the transition
 * and can leave nothing on screen at all. Anything that drives native navigation without a
 * tap behind it — the organisation swap's required-info sheet being the case this was
 * written for — waits here rather than racing.
 *
 * Always resolves, and on the timeout resolves anyway: a flag that somehow sticks must not
 * be able to wedge navigation permanently, which is the failure this whole gate exists to
 * prevent.
 */
export function whenNativeNavSettled(timeoutMs = 10_000): Promise<void> {
  if (!isNativeNavTransitionInFlight()) return Promise.resolve();
  return new Promise((resolve) => {
    const settle = () => {
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    const unsubscribe = subscribeNativeRootState(() => {
      if (!isNativeNavTransitionInFlight()) settle();
    });
  });
}
